// FORFEO INC. — Lot 4R-I
// Contrat pur des visites virtuelles Reserve.
// Explore/Reserve gèrent les tours, scènes, médias et points interactifs.
// Giftshop demeure l'autorité exclusive des prix, paiements, passes et QR.

export const FORFEO_RESERVE_VIRTUAL_TOUR_SCHEMA =
  'forfeo_reserve_virtual_tour_v1' as const;

export const FORFEO_RESERVE_VIRTUAL_TOUR_ACTIONS = [
  'public_snapshot',
  'merchant_snapshot',
  'command',
  'create_upload',
  'complete_upload',
] as const;

export const FORFEO_RESERVE_VIRTUAL_TOUR_COMMANDS = [
  'create_tour',
  'update_tour',
  'publish_tour',
  'archive_tour',
  'create_scene',
  'update_scene',
  'delete_scene',
  'reorder_scenes',
  'create_hotspot',
  'update_hotspot',
  'delete_hotspot',
  'delete_media',
] as const;

export const FORFEO_RESERVE_VIRTUAL_TOUR_MEDIA_KINDS = [
  'panorama_image',
  'standard_image',
  'video',
] as const;

export const FORFEO_RESERVE_VIRTUAL_TOUR_HOTSPOT_KINDS = [
  'scene',
  'reserve',
  'information',
  'external',
] as const;

export type ForfeoReserveVirtualTourAction =
  typeof FORFEO_RESERVE_VIRTUAL_TOUR_ACTIONS[number];

export type ForfeoReserveVirtualTourCommandKind =
  typeof FORFEO_RESERVE_VIRTUAL_TOUR_COMMANDS[number];

export type ForfeoReserveVirtualTourMediaKind =
  typeof FORFEO_RESERVE_VIRTUAL_TOUR_MEDIA_KINDS[number];

export type ForfeoReserveVirtualTourHotspotKind =
  typeof FORFEO_RESERVE_VIRTUAL_TOUR_HOTSPOT_KINDS[number];

export const FORFEO_RESERVE_VIRTUAL_TOUR_AUTHORITIES = {
  discovery: 'EXPLORE',
  userInterface: 'EXPLORE',
  mediaCatalog: 'RESERVE',
  tourPublication: 'RESERVE',
  scenes: 'RESERVE',
  hotspots: 'RESERVE',
  availability: 'RESERVE',
  holds: 'RESERVE',
  bookings: 'RESERVE',
  prices: 'GIFTSHOP',
  taxes: 'GIFTSHOP',
  fees: 'GIFTSHOP',
  payments: 'GIFTSHOP',
  refunds: 'GIFTSHOP',
  passes: 'GIFTSHOP',
  qr: 'GIFTSHOP',
} as const;

export const FORFEO_RESERVE_VIRTUAL_TOUR_MAX_UPLOAD_BYTES = {
  panorama_image: 30 * 1024 * 1024,
  standard_image: 15 * 1024 * 1024,
  video: 150 * 1024 * 1024,
} as const;

export interface ForfeoReserveVirtualTourMedia {
  id: string;
  kind: ForfeoReserveVirtualTourMediaKind;
  mimeType: string;
  bytes: number;
  width?: number;
  height?: number;
  durationSeconds?: number;
  signedUrl: string;
  posterUrl?: string;
  altText: string;
}

export interface ForfeoReserveVirtualTourHotspot {
  id: string;
  kind: ForfeoReserveVirtualTourHotspotKind;
  label: string;
  xPercent: number;
  yPercent: number;
  targetSceneId?: string;
  catalogItemId?: string;
  informationText?: string;
  externalUrl?: string;
  sortOrder: number;
}

export interface ForfeoReserveVirtualTourScene {
  id: string;
  title: string;
  description?: string;
  sortOrder: number;
  initialYaw: number;
  media: ForfeoReserveVirtualTourMedia;
  hotspots: ForfeoReserveVirtualTourHotspot[];
}

export interface ForfeoReserveVirtualTourSnapshot {
  schema: typeof FORFEO_RESERVE_VIRTUAL_TOUR_SCHEMA;
  authority: 'RESERVE';
  tourId: string;
  businessId: string;
  title: string;
  description?: string;
  locale: 'fr-CA' | 'en-CA';
  status: 'draft' | 'published' | 'archived';
  revision: number;
  startSceneId: string;
  scenes: ForfeoReserveVirtualTourScene[];
  generatedAt: string;
}

export interface ForfeoReserveVirtualTourCommand {
  kind: ForfeoReserveVirtualTourCommandKind;
  businessId: string;
  tourId?: string;
  sceneId?: string;
  hotspotId?: string;
  mediaId?: string;
  expectedRevision?: number;
  title?: string;
  description?: string;
  locale?: 'fr-CA' | 'en-CA';
  mediaKind?: ForfeoReserveVirtualTourMediaKind;
  mediaAltText?: string;
  sceneOrder?: string[];
  hotspotKind?: ForfeoReserveVirtualTourHotspotKind;
  label?: string;
  xPercent?: number;
  yPercent?: number;
  targetSceneId?: string;
  catalogItemId?: string;
  informationText?: string;
  externalUrl?: string;
  idempotencyKey: string;
  correlationId: string;
}

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const forbiddenFinancialFields = new Set([
  'amount',
  'amountCents',
  'amount_cents',
  'price',
  'priceId',
  'price_id',
  'tax',
  'taxes',
  'fee',
  'fees',
  'deposit',
  'currency',
  'stripe',
  'stripeId',
  'stripe_id',
  'paymentIntent',
  'payment_intent',
  'checkoutSession',
  'checkout_session',
  'qrSecret',
  'qr_secret',
]);

export function assertNoVirtualTourFinancialAuthority(
  value: unknown,
  path = 'payload',
): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      assertNoVirtualTourFinancialAuthority(entry, `${path}[${index}]`)
    );
    return;
  }
  if (!value || typeof value !== 'object') return;

  for (const [key, child] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (forbiddenFinancialFields.has(key)) {
      throw new Error(`forbidden_financial_field:${path}.${key}`);
    }
    assertNoVirtualTourFinancialAuthority(child, `${path}.${key}`);
  }
}

export function validateVirtualTourCommand(
  command: ForfeoReserveVirtualTourCommand,
): string[] {
  const errors: string[] = [];

  if (!FORFEO_RESERVE_VIRTUAL_TOUR_COMMANDS.includes(command.kind)) {
    errors.push('invalid_kind');
  }
  if (!uuidPattern.test(command.businessId)) errors.push('invalid_business_id');
  if (command.tourId && !uuidPattern.test(command.tourId)) {
    errors.push('invalid_tour_id');
  }
  if (command.sceneId && !uuidPattern.test(command.sceneId)) {
    errors.push('invalid_scene_id');
  }
  if (command.hotspotId && !uuidPattern.test(command.hotspotId)) {
    errors.push('invalid_hotspot_id');
  }
  if (command.mediaId && !uuidPattern.test(command.mediaId)) {
    errors.push('invalid_media_id');
  }
  if (
    command.expectedRevision !== undefined
    && (!Number.isInteger(command.expectedRevision)
      || command.expectedRevision < 1)
  ) {
    errors.push('invalid_revision');
  }
  if (
    typeof command.idempotencyKey !== 'string'
    || command.idempotencyKey.trim().length < 16
    || command.idempotencyKey.length > 160
  ) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) {
    errors.push('invalid_correlation_id');
  }
  if (
    command.xPercent !== undefined
    && (!Number.isFinite(command.xPercent)
      || command.xPercent < 0
      || command.xPercent > 100)
  ) {
    errors.push('invalid_x_percent');
  }
  if (
    command.yPercent !== undefined
    && (!Number.isFinite(command.yPercent)
      || command.yPercent < 0
      || command.yPercent > 100)
  ) {
    errors.push('invalid_y_percent');
  }
  if (command.targetSceneId && !uuidPattern.test(command.targetSceneId)) {
    errors.push('invalid_target_scene_id');
  }
  if (command.catalogItemId && !uuidPattern.test(command.catalogItemId)) {
    errors.push('invalid_catalog_item_id');
  }
  if (
    command.title !== undefined
    && (command.title.trim().length < 2 || command.title.length > 120)
  ) {
    errors.push('invalid_title');
  }
  if (
    command.label !== undefined
    && (command.label.trim().length < 2 || command.label.length > 100)
  ) {
    errors.push('invalid_label');
  }
  if (
    command.externalUrl
    && !/^https:\/\//i.test(command.externalUrl)
  ) {
    errors.push('invalid_external_url');
  }
  if (command.kind !== 'create_tour' && !command.tourId) {
    errors.push('tour_id_required');
  }
  if (
    ['publish_tour', 'archive_tour', 'create_scene', 'update_scene',
      'delete_scene', 'reorder_scenes', 'create_hotspot', 'update_hotspot',
      'delete_hotspot', 'delete_media'].includes(command.kind)
    && command.expectedRevision === undefined
  ) {
    errors.push('revision_required');
  }
  if (command.kind === 'create_scene' && !command.mediaId) {
    errors.push('media_id_required');
  }
  if (command.kind === 'create_hotspot') {
    if (!command.sceneId) errors.push('scene_id_required');
    if (!command.hotspotKind) errors.push('hotspot_kind_required');
    if (command.xPercent === undefined || command.yPercent === undefined) {
      errors.push('hotspot_position_required');
    }
    if (command.hotspotKind === 'scene' && !command.targetSceneId) {
      errors.push('target_scene_required');
    }
    if (command.hotspotKind === 'reserve' && !command.catalogItemId) {
      errors.push('catalog_item_required');
    }
    if (command.hotspotKind === 'information' && !command.informationText) {
      errors.push('information_text_required');
    }
    if (command.hotspotKind === 'external' && !command.externalUrl) {
      errors.push('external_url_required');
    }
  }
  try {
    assertNoVirtualTourFinancialAuthority(command);
  } catch {
    errors.push('financial_authority_forbidden');
  }
  return [...new Set(errors)];
}
